/**
 * UrbanNetworkTabbedPanel - Panel de red urbana canónica
 *
 * @version 3.0
 * @created 2026-03-03
 * @updated 2026-03-04 - Simplificado a solo Segmentos
 * @module core/layouts/panels/LeftPanel
 *
 * @description
 * Panel completo de red urbana que incluye:
 * 1. Lista de segmentos (edges) con códigos generados por sistema H3 R9
 * 2. Panel de control: Power, Eye, estadísticas de red
 * 3. Botón "Editar Estilos"
 * 4. Herramientas de edición: Crear rama, Dividir, Mover, Eliminar
 *
 * Los códigos de segmentos se generan usando el punto medio del edge
 * como referencia en la malla H3 R9.
 */

import { useCallback, useMemo, useState } from 'react';
import { Network, Search, X, Download } from 'lucide-react';
import { NetworkEdgesTab } from './NetworkEdgesTab';
import { canonicalNetworkApi } from '../../../spatial/services/canonical-network-api.service';
// UrbanNetworkPanel eliminado - ahora está integrado en NetworksAndGridsPanel
import { LeftPanelToggle } from './LeftPanel';
import { useRightPanel } from '../RightPanel/RightPanelContext';
import { useMapTools } from '../../../map-tools';
import { useUrbanNetworkSafe } from '../../../spatial/UrbanNetworkContext';
import { useProject } from '../../../projects';
import { getUrbanNetworkHeadlineMetrics } from './urban-network-metrics';
import './tabbed-left-panel.css';

export function UrbanNetworkTabbedPanel(): JSX.Element {
  const { canChangeContent } = useRightPanel();
  const { selectElement, getSelectedElement } = useMapTools();
  const urbanNetwork = useUrbanNetworkSafe();
  const { projectId, availableProjects } = useProject();

  const selectionKey = 'network-edges';
  const activeElementId = getSelectedElement(selectionKey);

  // ── Search ──
  const [searchQuery, setSearchQuery] = useState('');

  // ── Export ──
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async (): Promise<void> => {
    if (!projectId || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const blob = await canonicalNetworkApi.exportNetwork(projectId);
      const date = new Date().toISOString().slice(0, 10);
      const filename = `wayline_red_urbana_${projectId.slice(0, 8)}_${date}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('[UrbanNetworkTabbedPanel] exportNetwork error:', err);
      const status: number | undefined = err?.response?.status ?? err?.status;
      if (status === 400) {
        setExportError('Sin red urbana. Genera la red antes de exportar.');
      } else if (status === 403) {
        setExportError('Sin acceso a este proyecto.');
      } else if (status === 404) {
        setExportError('Proyecto no encontrado.');
      } else {
        setExportError('Error al exportar. Intenta de nuevo.');
      }
    } finally {
      setExporting(false);
    }
  }, [projectId, exporting]);

  // ── Stats derivadas — todos datos reales de contextos existentes ──
  const networkStats = urbanNetwork?.networkStats ?? null;
  const loadingStats = urbanNetwork?.loadingStats ?? false;
  const currentProject = useMemo(
    () => availableProjects.find(project => project.id === projectId) ?? null,
    [availableProjects, projectId]
  );
  const headlineMetrics = useMemo(
    () => getUrbanNetworkHeadlineMetrics(networkStats, currentProject),
    [networkStats, currentProject]
  );

  /**
   * Maneja el clic en el código de un elemento
   *
   * @remarks
   * Antes de cambiar de elemento, verifica si hay cambios sin guardar en el panel derecho.
   * Si hay cambios, pregunta al usuario si desea descartarlos.
   * Solo cambia de elemento si el usuario confirma o no hay cambios.
   *
   * Despacha un evento personalizado que puede ser manejado por:
   * - MapLayout para selección y apertura del panel derecho
   * - Integración con el sistema de selección del módulo
   */
  const handleCodeClick = useCallback(async (elementId: string): Promise<void> => {
    console.log('[UrbanNetworkTabbedPanel] ====================================');
    console.log(`[UrbanNetworkTabbedPanel] handleCodeClick - Segmento clickeado: ${elementId}`);

    // Verificar si hay cambios sin guardar ANTES de cambiar
    console.log('[UrbanNetworkTabbedPanel] Verificando cambios sin guardar...');
    const canChange = await canChangeContent();

    if (!canChange) {
      console.warn('[UrbanNetworkTabbedPanel] ✗ Usuario canceló el cambio (hay cambios sin guardar)');
      console.log('[UrbanNetworkTabbedPanel] ====================================');
      return; // Usuario canceló, no cambiar
    }

    console.log('[UrbanNetworkTabbedPanel] ✓ Cambios verificados, procediendo con el cambio...');

    // Marcar elemento como seleccionado para resaltado en sidebar
    selectElement(selectionKey, elementId);

    // Actualizar selección en contexto de red urbana para highlight visual en mapa
    if (urbanNetwork) {
      urbanNetwork.setSelectedEdgeId(elementId);
      console.log(`[UrbanNetworkTabbedPanel] ✓ Edge seleccionado en contexto: ${elementId}`);
    }

    console.log(`[UrbanNetworkTabbedPanel] Despachando evento: network-edges-select`);

    // Despachar evento para que otros componentes puedan reaccionar
    window.dispatchEvent(new CustomEvent('network-edges-select', {
      detail: { elementId, elementType: 'edges' },
    }));

    console.log('[UrbanNetworkTabbedPanel] ====================================');
  }, [canChangeContent, selectElement, selectionKey, urbanNetwork]);

  return (
    <div className="tabbed-left-panel">
      {/* Contenido: Lista de segmentos */}
      <div className="content-card-wrapper">
        <div className="content-card">

          {/* ── Header del módulo ── */}
          <div className="un-panel-header">
            <div className="un-panel-header-icon">
              <Network size={18} />
            </div>
            <div className="un-panel-header-info">
              <span className="un-panel-header-title">Red Urbana</span>
              <span className="un-panel-header-sub">
                {projectId ? 'Sistema vial canónico' : 'Sin proyecto activo'}
              </span>
            </div>
          </div>

          {/* ── Estadísticas globales del módulo Red Urbana ── */}
          <div className="un-stats-grid">
            <div className="un-stat-card un-stat-card--cyan" title={headlineMetrics.totalLength.title}>
              <span className={`un-stat-value${loadingStats ? ' un-stat-value--loading' : ''}`}>
                {loadingStats ? '…' : headlineMetrics.totalLength.value}
              </span>
              <span className="un-stat-label">Longitud Red</span>
              {!loadingStats && headlineMetrics.totalLength.subtext && (
                <span className="un-stat-sub">{headlineMetrics.totalLength.subtext}</span>
              )}
            </div>
            <div className="un-stat-card un-stat-card--amber" title={headlineMetrics.segments.title}>
              <span className={`un-stat-value${loadingStats ? ' un-stat-value--loading' : ''}`}>
                {loadingStats ? '…' : headlineMetrics.segments.value}
              </span>
              <span className="un-stat-label">Segmentos</span>
            </div>
            <div className="un-stat-card un-stat-card--green" title={headlineMetrics.intersections.title}>
              <span className={`un-stat-value${loadingStats ? ' un-stat-value--loading' : ''}`}>
                {loadingStats ? '…' : headlineMetrics.intersections.value}
              </span>
              <span className="un-stat-label">Intersecciones</span>
            </div>
            <div className="un-stat-card un-stat-card--coral" title={headlineMetrics.roadDensity.title}>
              <span className={`un-stat-value${loadingStats ? ' un-stat-value--loading' : ''}`}>
                {loadingStats ? '…' : headlineMetrics.roadDensity.value}
              </span>
              <span className="un-stat-label">Densidad Vial</span>
              {!loadingStats && headlineMetrics.roadDensity.subtext && (
                <span className="un-stat-sub">{headlineMetrics.roadDensity.subtext}</span>
              )}
            </div>
          </div>

          {/* ── Export button ── */}
          <div className="un-export-row">
            <button
              className={`un-export-btn${exporting ? ' un-export-btn--loading' : ''}`}
              onClick={handleExport}
              disabled={!projectId || exporting}
              title="Exportar red urbana canónica como ZIP (dos GeoJSON separados)"
            >
              <Download size={13} />
              <span>{exporting ? 'Exportando…' : 'Exportar red urbana'}</span>
            </button>
            {exportError && (
              <p className="un-export-error">{exportError}</p>
            )}
          </div>

          {/* ── Search bar ── */}
          <div className="un-search-bar">
            <Search size={13} className="un-search-icon" />
            <input
              className="un-search-input"
              type="text"
              placeholder="Buscar segmento, código o tipo…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              aria-label="Buscar segmento"
            />
            {searchQuery && (
              <button
                className="un-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Limpiar búsqueda"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className="tab-content">
            <NetworkEdgesTab
              onCodeClick={handleCodeClick}
              activeElementId={activeElementId}
              searchQuery={searchQuery}
            />
          </div>
          <LeftPanelToggle />
        </div>
      </div>
      {/* Panel de control eliminado - ahora está en NetworksAndGridsPanel */}
    </div>
  );
}
